import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { FaExternalLinkAlt, FaTimes } from "react-icons/fa";
import { formatBatchDateTime } from "./inquiryBatchUtils";

function InquiryBatchViewReplyModal({ open, item, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !item) return null;

  const reply = item.latest_reply || null;
  const docs = Array.isArray(reply?.documents) ? reply.documents : [];

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-3 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="view-inquiry-reply-title"
        onClick={onClose}
      >
        <div
          className="tailwind-container relative z-10 w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
            <div className="min-w-0">
              <h2
                id="view-inquiry-reply-title"
                className="mb-0 text-[15px] font-semibold text-slate-900"
              >
                Provider reply
              </h2>
              <p className="mb-0 mt-0.5 text-[12px] text-slate-500">
                {item.student_name || "Student"}
                {item.provider_name ? ` · ${item.provider_name}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
              style={{ boxShadow: "none" }}
              aria-label="Close"
            >
              <FaTimes size={12} />
            </button>
          </div>

          <div className="space-y-3 px-4 py-3">
            {!reply ? (
              <p className="mb-0 text-[13px] text-slate-500">No reply saved.</p>
            ) : (
              <>
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Notes
                  </p>
                  <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                    <p className="mb-0 whitespace-pre-wrap text-[13px] text-slate-800">
                      {reply.notes || "—"}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Documents
                  </p>
                  {docs.length > 0 ? (
                    <ul className="space-y-1.5">
                      {docs.map((doc) => {
                        const href =
                          doc.url || doc.file_url || doc.file_path || null;
                        const label =
                          doc.original_name || doc.file_name || "Document";
                        return (
                          <li key={doc.id || label}>
                            {href ? (
                              <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#007BFF] hover:underline"
                              >
                                <FaExternalLinkAlt size={10} />
                                {label}
                              </a>
                            ) : (
                              <span className="text-[13px] text-slate-700">
                                {label}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="mb-0 text-[13px] text-slate-500">
                      No documents attached
                    </p>
                  )}
                </div>

                {reply.created_at ? (
                  <p className="mb-0 text-[11px] text-slate-400">
                    Saved {formatBatchDateTime(reply.created_at)}
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div className="flex justify-end border-t border-slate-100 px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-md border border-slate-200 bg-white px-3 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
              style={{ boxShadow: "none" }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default InquiryBatchViewReplyModal;
